import { EndpointSetting } from "@/constants/app/type";
import { HotelRoom, HotelState } from "../type";
import { resolveHotelFeePolicies } from "@/utils/hotel/resolveHotelFeePolicies";
import { parseResourceRoom } from "@/utils/hotel/parseResouceRoom";
import { parseFacilitiesHotel } from "@/utils/hotel/parseFacilitiesHotel";
import {
  hydrateHotelFormSearchFromApi,
  pickHotelApiSearchCriteria,
} from "@/utils/hotel/hydrateHotelFormSearchFromApi";

const shoppingSignals = ['aeon', 'chợ', 'siêu thị', 'mua sắm', 'trung tâm thương mại', 'cửa hàng', 'mall', 'store', 'shopping', 'market']
const builingSignals = ['tòa nhà', 'building', 'tower', 'plaza', 'center', 'centre', 'landmark']
const outsideSignals = ['công viên', 'garden', 'playground']
const hospitalSignals = ['bệnh viện', 'phòng khám', 'clinic', 'hospital', 'medical']

const getCategoryName = (name: string) => {
  if (shoppingSignals.some(signal => name.includes(signal))) {
    return 'Nơi mua sắm'
  }
  if (hospitalSignals.some(signal => name.includes(signal))) {
    return 'Bệnh viện & phòng khám'
  }
  if (builingSignals.some(signal => name.includes(signal))) {
    return 'building'
  }
  if (outsideSignals.some(signal => name.includes(signal))) {
    return 'outside'
  }
  return 'Khác'
}

const parseLocation = (location: any) => {
  if (!location.categoryName) {
    return {
      ...location,
      categoryName: location.categoryName || getCategoryName(location.name.toLowerCase())
    }
  }
  return location
}

export const callbackHotelDetail: EndpointSetting['caller'] = (
  state: HotelState,
  action: any,
  endpointName: any
) => {
  state.hotels_detail.loading = false
  state.hotels_detail.error = null
  state.hotels_detail.success = true

  const hotel = action.payload
  const { feePolicies } = action.payload || {}

  state.hotels_detail.feePolicies = resolveHotelFeePolicies(
    feePolicies,
    state.hotels_search.feePolicies,
    state.hotels_detail.feePolicies,
  )

  const searchCriteria = pickHotelApiSearchCriteria(hotel)
  if (searchCriteria) {
    hydrateHotelFormSearchFromApi(state.formSearch, searchCriteria)
  }

  const facilities = parseFacilitiesHotel(hotel)
  const sourceRooms = hotel?.searchResponse?.rooms
  const roomsList = Array.isArray(sourceRooms)
    ? sourceRooms
    : Object.values(sourceRooms || {})

  const reducedRooms = roomsList
    .reduce((obj: any, room: HotelRoom) => {
      let roomResources: any = (state.hotels_detail_resource.data?.roomData || []).find((d: any) => `${d.hotelRoomTypeId}` == `${room.roomId}`)
      roomResources = parseResourceRoom(roomResources, facilities)

      if (!obj[room.roomId]) {
        obj[room.roomId] = [
          {
            ...room,
            index: 1,
            resource: roomResources
          }
        ]
      } else {
        obj[room.roomId].push({
          ...room,
          index: obj[room.roomId].length + 1,
          resource: roomResources
        })
      }
      return obj
    }, {} as any)

  const nearbyProperty = (Array.isArray(hotel?.localInfo?.nearbyProperties?.nearbyProperty)
    ? hotel.localInfo.nearbyProperties.nearbyProperty
    : typeof hotel?.localInfo?.nearbyProperties?.nearbyProperty === 'undefined'
      ? []
      : [hotel?.localInfo?.nearbyProperties?.nearbyProperty]).map(parseLocation)
  const nearbyPlace = (Array.isArray(hotel?.localInfo?.nearbyPlaces?.nearbyPlace)
    ? hotel.localInfo.nearbyPlaces.nearbyPlace
    : typeof hotel?.localInfo?.nearbyPlaces?.nearbyPlace === 'undefined'
      ? []
      : [hotel?.localInfo?.nearbyPlaces?.nearbyPlace]).map(parseLocation)

  const topPlace = (Array.isArray(hotel?.localInfo?.topPlaces?.topPlace)
    ? hotel.localInfo.topPlaces.topPlace
    : typeof hotel?.localInfo?.topPlaces?.topPlace === 'undefined'
      ? []
      : [hotel?.localInfo?.topPlaces?.topPlace]).map(parseLocation)

  const [canWalkings, others] = nearbyPlace.concat(nearbyProperty)
    .reduce(([canWalkings, others]: [any[], any[]], location: any) => {
      const { distance, name, categoryName } = location
      if (!!name && distance <= 0.5) {
        canWalkings.push(location)
      } else if (!!name && distance <= 1.5 && categoryName !== "Bệnh viện & phòng khám") {
        others.push(location)
      }
      return [canWalkings, others]
    }, [[], []])
    .map((arr: any[]) => arr.sort(({ categoryName }: any) => {
      if (categoryName === 'Nơi mua sắm') {
        return -1
      }
      return 1
    }))

  state.hotels_detail.data = {
    ...hotel,
    localInfo: {
      ...hotel.localInfo,
      canWalkings: canWalkings,
      others: others,
      nearbyProperties: {
        nearbyProperty: nearbyProperty,
      },
      nearbyPlaces: {
        nearbyPlace: nearbyPlace
      },
      topPlaces: {
        topPlace: topPlace
      }
    },
    searchResponse: {
      ...hotel.searchResponse,
      roomIds: Object.keys(reducedRooms).sort((roomId1, roomId2) => {
        const cheapestRoom1: HotelRoom | undefined = reducedRooms[roomId1]?.sort((r1: HotelRoom, r2: HotelRoom) => r1.perRoomPerNightRate.inclusive - r2.perRoomPerNightRate.inclusive)[0]
        const cheapestRoom2: HotelRoom | undefined = reducedRooms[roomId2]?.sort((r3: HotelRoom, r4: HotelRoom) => r3.perRoomPerNightRate.inclusive - r4.perRoomPerNightRate.inclusive)[0]
        if (!cheapestRoom1 || !cheapestRoom2) return 0
        return cheapestRoom1.perRoomPerNightRate.inclusive - cheapestRoom2.perRoomPerNightRate.inclusive
      }),
      rooms: reducedRooms
    },
    facilities
  }
}